import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { signInWithCustomToken } from 'firebase/auth';
import { auth } from '../utils/firebase';
const FUNCTIONS_BASE = 'https://africa-south1-insightprofinal-3c793.cloudfunctions.net';

async function callFunction(name, body) {
  const res = await fetch(`${FUNCTIONS_BASE}/${name}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return res.json();
}

const GOLD      = 'var(--gold, #D4A017)';
const BG        = '#0a0a0a';
const CARD_BG   = '#141414';
const CARD_BORDER = '#2a2a2a';

// ── Spinner ────────────────────────────────────────────────
function Spinner() {
  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:16 }}>
      <div style={{
        width:40, height:40, border:`3px solid #2a2a2a`,
        borderTop:`3px solid ${GOLD}`, borderRadius:'50%',
        animation:'spin 0.9s linear infinite',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ── Password setup form ────────────────────────────────────
function PasswordForm({ onSubmit, loading }) {
  const [pw,  setPw]  = useState('');
  const [pw2, setPw2] = useState('');
  const [err, setErr] = useState('');

  const input = {
    width:'100%', padding:'12px 14px', borderRadius:8,
    border:`1px solid ${CARD_BORDER}`, background:'#1a1a1a',
    color:'#ffffff', fontSize:15, outline:'none', boxSizing:'border-box',
  };

  const handleSubmit = () => {
    if (pw.length < 8) { setErr('Password must be at least 8 characters.'); return; }
    if (pw !== pw2)    { setErr('Passwords do not match.'); return; }
    setErr('');
    onSubmit(pw);
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
      <p style={{ color:'#aaaaaa', fontSize:14, margin:0, lineHeight:1.6 }}>
        Set a password to secure your account. You'll use this to sign in going forward.
      </p>
      <input
        type="password"
        placeholder="New password"
        value={pw}
        onChange={e => setPw(e.target.value)}
        style={input}
        autoFocus
      />
      <input
        type="password"
        placeholder="Confirm password"
        value={pw2}
        onChange={e => setPw2(e.target.value)}
        style={input}
        onKeyDown={e => e.key === 'Enter' && handleSubmit()}
      />
      {err && <p style={{ color:'#e05252', fontSize:13, margin:0 }}>{err}</p>}
      <button
        onClick={handleSubmit}
        disabled={loading}
        style={{
          background: loading ? '#555' : GOLD,
          color:'#000', border:'none', borderRadius:8,
          padding:'13px 0', fontWeight:700, fontSize:15,
          cursor: loading ? 'not-allowed' : 'pointer',
          marginTop:4, width:'100%',
        }}
      >
        {loading ? 'Setting up…' : 'Activate Account →'}
      </button>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────
export default function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const navigate       = useNavigate();
  const token          = searchParams.get('token');

  const [phase,   setPhase]   = useState('verifying'); // verifying | setPassword | success | error
  const [message, setMessage] = useState('');
  const [customToken, setCustomToken] = useState(null);
  const [email,       setEmail]       = useState('');
  const [pwLoading,   setPwLoading]   = useState(false);

  // Step 1 — verify token with Cloud Function
  useEffect(() => {
    if (!token) {
      setPhase('error');
      setMessage('No invite token found in this link. Please check the email and try again.');
      return;
    }

    (async () => {
      try {
        const res = await callFunction('verifyInviteToken', { token });
        if (!res.success) throw new Error(res.error || 'Verification failed');
        setCustomToken(res.customToken);
        setEmail(res.email || '');
        setPhase('setPassword');
      } catch (e) {
        setPhase('error');
        setMessage(e.message || 'This invite link is invalid or has expired.');
      }
    })();
  }, [token]);

  // Step 2 — set password then sign in with custom token
  const handlePasswordSet = async (password) => {
    setPwLoading(true);
    try {
      // Sign in with the custom token first
      const cred = await signInWithCustomToken(auth, customToken);

      // Link email+password credential so user can sign in with password next time
      // Using linkWithCredential is correct here: the account was created with no
      // password, so we're linking a new email/password provider to it.
      const { EmailAuthProvider, linkWithCredential } = await import('firebase/auth');
      const credential = EmailAuthProvider.credential(cred.user.email, password);
      try {
        await linkWithCredential(cred.user, credential);
      } catch (linkErr) {
        // If email/password is already linked (re-invite case), fall back to updatePassword
        if (linkErr.code === 'auth/provider-already-linked' || linkErr.code === 'auth/email-already-in-use') {
          const { updatePassword } = await import('firebase/auth');
          await updatePassword(cred.user, password);
        } else {
          throw linkErr;
        }
      }

      setPhase('success');
      setTimeout(() => navigate('/', { replace: true }), 1800);
    } catch (e) {
      setPhase('error');
      setMessage(
        e.code === 'auth/requires-recent-login'
          ? 'Session error. Please request a new invite link.'
          : e.message || 'Could not activate your account. Please contact your administrator.'
      );
    } finally {
      setPwLoading(false);
    }
  };

  // ── Layout wrapper ────────────────────────────────────────
  const Card = ({ children }) => (
    <div style={{
      minHeight:'100vh', background:BG,
      display:'flex', alignItems:'center', justifyContent:'center',
      padding:'24px 16px',
    }}>
      <div style={{
        background:CARD_BG, border:`1px solid ${CARD_BORDER}`,
        borderRadius:16, padding:'40px 32px', width:'100%', maxWidth:420,
      }}>
        {/* Logo */}
        <div style={{ textAlign:'center', marginBottom:32 }}>
          <span style={{
            fontSize:22, fontWeight:700, color:GOLD, letterSpacing:'0.02em',
          }}>InsightPro™</span>
        </div>
        {children}
      </div>
    </div>
  );

  // ── Phases ────────────────────────────────────────────────
  if (phase === 'verifying') return (
    <Card>
      <div style={{ textAlign:'center' }}>
        <Spinner />
        <p style={{ color:'#aaaaaa', fontSize:14, marginTop:20 }}>Verifying your invite link…</p>
      </div>
    </Card>
  );

  if (phase === 'setPassword') return (
    <Card>
      <h2 style={{ color:'#ffffff', fontSize:20, margin:'0 0 8px', textAlign:'center' }}>
        Welcome to InsightPro™
      </h2>
      {email && (
        <p style={{ color:'#666', fontSize:13, textAlign:'center', margin:'0 0 24px' }}>
          {email}
        </p>
      )}
      <PasswordForm onSubmit={handlePasswordSet} loading={pwLoading} />
    </Card>
  );

  if (phase === 'success') return (
    <Card>
      <div style={{ textAlign:'center' }}>
        <div style={{ fontSize:48, marginBottom:16 }}>✓</div>
        <h2 style={{ color:'#ffffff', fontSize:20, margin:'0 0 12px' }}>Account activated!</h2>
        <p style={{ color:'#aaaaaa', fontSize:14 }}>Taking you to your dashboard…</p>
      </div>
    </Card>
  );

  if (phase === 'error') return (
    <Card>
      <div style={{ textAlign:'center' }}>
        <div style={{ fontSize:48, marginBottom:16 }}>✕</div>
        <h2 style={{ color:'#e05252', fontSize:18, margin:'0 0 12px' }}>Invite link invalid</h2>
        <p style={{ color:'#aaaaaa', fontSize:14, lineHeight:1.6, margin:'0 0 24px' }}>
          {message}
        </p>
        <a href="/home" style={{
          display:'inline-block', padding:'11px 28px', borderRadius:8,
          background:GOLD, color:'#000', fontWeight:700, fontSize:14,
          textDecoration:'none',
        }}>
          Go to InsightPro
        </a>
      </div>
    </Card>
  );

  return null;
}
